/* eslint-disable react/no-unescaped-entities */
import React from 'react';
import { Shield } from 'lucide-react';
import Seo from '../components/Seo';

const PrivacyPage = () => {
  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <Seo
        title="Privacy Policy – MyCBC"
        description="How MyCBC protects and handles your private health data."
        path="/privacy"
      />
      <div className="flex items-center gap-3 mb-6">
        <Shield className="h-8 w-8 text-primary" />
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900">Privacy Policy</h1>
      </div>

      <div className="bg-white rounded-2xl p-6 md:p-8 border border-slate-100 shadow-sm space-y-6">
        <div className="prose prose-slate max-w-none">
          <p className="text-sm text-slate-500">Last Updated: December 2025</p>

          <h2 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">1. Introduction</h2>
          <p className="text-slate-700 leading-relaxed">
            Blood Cancer Companion ("we," "our," or "the App") is committed to protecting your privacy. This Privacy Policy 
            explains how we collect, use, store, and protect your personal health information.
          </p>

          <h2 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">2. User-Entered and Self-Managed Data</h2>
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 my-4">
            <p className="text-slate-900 font-semibold mb-2">Important: Data Ownership and Responsibility</p>
            <ul className="list-disc list-inside space-y-2 text-slate-700">
              <li><strong>All health data in the App is user-entered and self-managed by you.</strong></li>
              <li>You retain full ownership and control of all data you enter into the App.</li>
              <li>You are responsible for the accuracy, completeness, and management of your data.</li>
              <li>We do not review, verify, validate, or modify the health information you enter.</li>
              <li>We do not share your data with healthcare providers, researchers, or third parties without your explicit consent.</li>
            </ul>
          </div>

          <h2 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">3. Information We Collect</h2>
          
          <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">3.1 Information You Provide</h3>
          <p className="text-slate-700 leading-relaxed">When you use the App, you voluntarily provide:</p>
          <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
            <li><strong>Account Information:</strong> Name, email address, password, country selection</li>
            <li><strong>Health Data:</strong> Blood test results, symptoms, medications, and related notes</li>
            <li><strong>Dates and Timestamps:</strong> When you enter or modify health information</li>
            <li><strong>User Preferences:</strong> Display settings, country-specific unit preferences</li>
          </ul>

          <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">3.2 Automatically Collected Information</h3>
          <p className="text-slate-700 leading-relaxed">We automatically collect limited technical information:</p>
          <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
            <li>Login timestamps and session information</li>
            <li>Device type and browser information (for technical support)</li>
            <li>Error logs and diagnostic data (for app improvements)</li>
          </ul>

          <h2 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">4. How We Use Your Information</h2>
          <p className="text-slate-700 leading-relaxed">We use your information solely to:</p>
          <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
            <li>Provide the App's core functionality (data storage, visualization, trend analysis)</li>
            <li>Generate personalized alerts based on your entered data</li>
            <li>Create PDF reports for your use</li>
            <li>Authenticate your account and maintain security</li>
            <li>Improve the App's performance and user experience</li>
            <li>Provide customer support when requested</li>
          </ul>

          <h2 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">5. How We Do NOT Use Your Information</h2>
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 my-4">
            <p className="text-slate-900 font-semibold mb-2">We Do NOT:</p>
            <ul className="list-disc list-inside space-y-2 text-slate-700">
              <li>Sell, rent, or trade your personal health information to third parties</li>
              <li>Use your data for advertising or marketing purposes</li>
              <li>Share your data with healthcare providers without your explicit permission</li>
              <li>Use your data for medical research without your informed consent</li>
              <li>Provide medical advice or diagnoses based on your data</li>
              <li>Access or review your health data except when necessary for technical support (with your permission)</li>
            </ul>
          </div>

          <h2 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">6. Data Storage and Security</h2>
          <p className="text-slate-700 leading-relaxed">We implement industry-standard security measures to protect your data:</p>
          <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
            <li><strong>Encryption:</strong> All data is encrypted in transit (HTTPS) and at rest</li>
            <li><strong>Authentication:</strong> Password-protected accounts with secure authentication</li>
            <li><strong>Access Controls:</strong> Strict access limitations to your personal data</li>
            <li><strong>Regular Backups:</strong> Automated backups to prevent data loss</li>
            <li><strong>Secure Infrastructure:</strong> Hosted on secure, reputable cloud infrastructure</li>
          </ul>

          <h2 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">7. Data Retention</h2>
          <p className="text-slate-700 leading-relaxed">
            Your health data is retained for as long as you maintain an active account. You can delete individual entries or 
            request full account deletion at any time. Upon account deletion, all your personal data will be permanently removed 
            from our systems within 30 days (except as required by law).
          </p>

          <h2 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">8. Your Rights and Choices</h2>
          <p className="text-slate-700 leading-relaxed">You have the right to:</p>
          <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
            <li><strong>Access:</strong> View all data you've entered at any time</li>
            <li><strong>Modify:</strong> Edit or update your health information</li>
            <li><strong>Delete:</strong> Remove individual entries or your entire account</li>
            <li><strong>Export:</strong> Download your data in PDF format</li>
            <li><strong>Port:</strong> Request a copy of your data in a structured format</li>
            <li><strong>Withdraw Consent:</strong> Stop using the App at any time</li>
          </ul>

          <h2 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">9. Data Sharing and Disclosure</h2>
          <p className="text-slate-700 leading-relaxed">
            We do not share your personal health information except in the following limited circumstances:
          </p>
          <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
            <li><strong>With Your Consent:</strong> When you explicitly authorize sharing (e.g., emailing a PDF report to your doctor)</li>
            <li><strong>Legal Requirements:</strong> If required by law, court order, or government regulation</li>
            <li><strong>Service Providers:</strong> With trusted third-party services that help us operate the App (e.g., cloud hosting), 
            under strict confidentiality agreements</li>
            <li><strong>Emergency Situations:</strong> If we believe disclosure is necessary to protect your health or safety</li>
          </ul>

          <h2 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">10. Children's Privacy</h2>
          <p className="text-slate-700 leading-relaxed">
            The App is not intended for use by individuals under the age of 18. We do not knowingly collect personal information 
            from children. If you believe a child has provided us with personal information, please contact us immediately.
          </p>

          <h2 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">11. International Users</h2>
          <p className="text-slate-700 leading-relaxed">
            If you access the App from outside your country of residence, please be aware that your information may be transferred 
            to, stored, and processed in the country where our servers are located. By using the App, you consent to this transfer.
          </p>

          <h2 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">12. Changes to This Privacy Policy</h2>
          <p className="text-slate-700 leading-relaxed">
            We may update this Privacy Policy from time to time. We will notify you of significant changes by email or through a 
            prominent notice in the App. Your continued use of the App after changes constitutes acceptance of the updated policy.
          </p>

          <h2 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">13. Contact Us</h2>
          <p className="text-slate-700 leading-relaxed">
            If you have questions, concerns, or requests regarding this Privacy Policy or your personal data, please contact us 
            through the App's support channels.
          </p>

          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mt-8">
            <p className="text-slate-700 text-sm">
              <strong>Summary:</strong> Your health data is user-entered and self-managed. You retain full ownership and control. 
              We use your data solely to provide the App's functionality and do not share it without your consent. We implement 
              strong security measures to protect your information. You can access, modify, or delete your data at any time.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
